import { useIntl } from 'react-intl';
import { Button } from 'primereact/button';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Tag } from 'primereact/tag';
import { ServiceStatisticsLongestWaitDTO } from '../../dto/ServiceStatisticsDTO';
import {
    formatDuration, LONG_SERVICE_MINUTES, readablePriority, shortDayLabel, splitLocalTime,
} from './periodDetail';
import { parseIsoDate } from './statisticsUtils';
import { LONGEST_WAITS_FIRST, SectionStatus } from './usePeriodDetail';

interface PeriodDetailLongestWaitsProps {
    rows: ServiceStatisticsLongestWaitDTO[];
    status: SectionStatus;
    /** Ya se pidieron las 20: no hay «Ver más». */
    expanded: boolean;
    /** Primer y último día del período que se mira (`yyyy-MM-dd`): el día solo se escribe cuando difiere de él. */
    rangeFrom: string;
    rangeTo: string;
    /** En la vista del período cada fila dice su servicio; en la de un servicio no se repite. */
    showService: boolean;
    onMore: () => void;
    onRetry: () => void;
}

/**
 * «Atenciones con más espera»: las atenciones que más esperaron en el período, de mayor a menor, sin datos de los
 * pacientes. Es una lista de lectura (las filas no se tocan): a la izquierda cuándo llegó y cuándo lo atendieron, y debajo
 * el servicio, el código y «Sobreturno»; a la derecha la espera en grande, la duración de la atención y, con texto, las
 * señales que ayudan a entender qué pasó («Llegó el día anterior», «Atención muy larga»). Si hay cinco, «Ver más» pide veinte.
 * TODO(design-system): una lista de solo lectura de dos renglones en un drawer no está en Novarium; se arma con las filas de
 * la lista de avisos (separador, alto holgado) y los tags de estado.
 */
export default function PeriodDetailLongestWaits({
    rows, status, expanded, rangeFrom, rangeTo, showService, onMore, onRetry,
}: PeriodDetailLongestWaitsProps) {
    const intl = useIntl();
    const singleDay = rangeFrom === rangeTo;
    const dayLabel = (date: string) => shortDayLabel(intl, parseIsoDate(date)).toLowerCase();

    if (status === 'failed') {
        return (
            <div className="ss-detail__failed" role="status">
                <p className="nv-note">
                    <i className="pi pi-info-circle" aria-hidden="true" />
                    {intl.formatMessage({ id: 'ServicesStatisticsPeriodDetailLongestFailed' })}
                </p>
                <Button type="button" link label={intl.formatMessage({ id: 'ServicesStatisticsRetry' })} onClick={onRetry} />
            </div>
        );
    }
    if (status === 'loading' && rows.length === 0) {
        return (
            <span className="nv-loading ss-detail__loading" role="status">
                <ProgressSpinner strokeWidth="4" style={{ width: 16, height: 16 }} />
                <span>{intl.formatMessage({ id: 'ServicesStatisticsLoading' })}</span>
            </span>
        );
    }
    if (rows.length === 0) {
        return (
            <p className="nv-note">
                <i className="pi pi-info-circle" aria-hidden="true" />
                {intl.formatMessage({ id: 'ServicesStatisticsPeriodDetailLongestEmpty' })}
            </p>
        );
    }

    return (
        <>
            <ul className="ss-detail__longest">
                {rows.map((row, index) => {
                    const arrival = splitLocalTime(row.arrivalTime);
                    const served = splitLocalTime(row.servedTime);
                    const otherDay = arrival.date !== served.date;
                    // El día se escribe cuando difiere del período que se mira (siempre en una semana o un mes, y siempre si
                    // la llegada y la atención son de días distintos, para que se entienda)
                    const when = (part: { date: string; time: string }) => (otherDay || !singleDay || part.date !== rangeFrom
                        ? intl.formatMessage({ id: 'ServicesStatisticsPeriodDetailLongestOnDayAt' }, { day: dayLabel(part.date), time: part.time })
                        : intl.formatMessage({ id: 'ServicesStatisticsPeriodDetailLongestAt' }, { time: part.time }));
                    const details = [
                        showService ? row.serviceName : null,
                        row.priorityName ? readablePriority(row.priorityName) : null,
                        row.overbooked ? intl.formatMessage({ id: 'ServicesStatisticsPeriodDetailLongestOverbooked' }) : null,
                        row.scheduledTime
                            ? intl.formatMessage({ id: 'ServicesStatisticsPeriodDetailLongestScheduled' }, { time: splitLocalTime(row.scheduledTime).time })
                            : null,
                    ].filter((item): item is string => item !== null);
                    return (
                        <li key={`${row.arrivalTime}-${row.serviceId}-${index}`} className="ss-detail__longest-row">
                            <span className="ss-detail__longest-main">
                                <span className="ss-detail__longest-when">
                                    {intl.formatMessage({ id: 'ServicesStatisticsPeriodDetailLongestRow' },
                                        { arrival: when(arrival), served: when(served) })}
                                </span>
                                {details.length > 0 && <span className="ss-detail__service-meta">{details.join(' · ')}</span>}
                            </span>
                            <span className="ss-detail__longest-wait">
                                <span className="ss-detail__service-minutes">{formatDuration(row.waitMinutes)}</span>
                                <span className="ss-detail__service-meta">
                                    {intl.formatMessage({ id: 'ServicesStatisticsPeriodDetailLongestService' },
                                        { value: formatDuration(row.serviceMinutes) })}
                                </span>
                                {otherDay && (
                                    <Tag className="nv-tag-neutral"
                                        value={intl.formatMessage({ id: 'ServicesStatisticsPeriodDetailLongestPrevDay' })} />
                                )}
                                {row.serviceMinutes >= LONG_SERVICE_MINUTES && (
                                    <Tag className="nv-tag-warn"
                                        value={intl.formatMessage({ id: 'ServicesStatisticsPeriodDetailLongestVeryLong' })} />
                                )}
                            </span>
                        </li>
                    );
                })}
            </ul>
            {!expanded && rows.length >= LONGEST_WAITS_FIRST && (
                <div className="ss-detail__more">
                    {status === 'loading' ? (
                        <span className="nv-loading" role="status">
                            <ProgressSpinner strokeWidth="4" style={{ width: 16, height: 16 }} />
                            <span>{intl.formatMessage({ id: 'ServicesStatisticsLoading' })}</span>
                        </span>
                    ) : (
                        <Button type="button" link label={intl.formatMessage({ id: 'ServicesStatisticsPeriodDetailLongestMore' })}
                            onClick={onMore} />
                    )}
                </div>
            )}
        </>
    );
}
